import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDisclosure } from '@withjoy/joykit';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { useTranslation } from '@shared/core';
import { useUserConsent } from '@shared/utils/userConsent';
import { addError } from '@shared/utils/logger';

export type PaymentMethodDisclaimerSignedMetadata = Readonly<{
  paymentMethod: string;
  paymentUsername: string;
  paymentLink: string;
}>;

export function useAppPaymentDisclaimerDialogController(eventId: string) {
  const { getUserConsent, setUserConsent } = useUserConsent();
  const [paymentMetadata, setPaymentMetadata] = useState<PaymentMethodDisclaimerSignedMetadata | null>(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const telemetry = useAdminRegistryTelemetry();
  const { t } = useTranslation('generalFormValidation');

  const { onOpen, onClose, isOpen } = useDisclosure();

  const disclaimerFormik = useFormik<{ agreementChecked: boolean; trackingChecked: boolean }>({
    initialValues: { agreementChecked: false, trackingChecked: false },
    onSubmit: async (_values, { resetForm }) => {
      telemetry.acceptCashFundDisclaimerClicked({
        paymentLink: paymentMetadata?.paymentLink || '',
        paymentMethod: paymentMetadata?.paymentMethod || '',
        paymentUsername: paymentMetadata?.paymentUsername || '',
      });

      if (getUserConsent('registryAppPaymentPlatformForCashFundsDisclaimerSigned') !== true) {
        try {
          await setUserConsent('registryAppPaymentPlatformForCashFundsDisclaimerSigned', true);
        } catch (err) {
          addError(err, { context: 'AppPaymentDisclaimerDialog.submit', eventId });
        }
      }

      setAgreementAccepted(true);
      resetForm();
      onClose();
    },
    validationSchema: Yup.object().shape({
      agreementChecked: Yup.boolean().oneOf([true], t('mustAgreeToContinue')()),
      trackingChecked: Yup.boolean().oneOf([true], t('mustAgreeToContinue')()),
    }),
  });

  const onOpenDisclaimerDialog = (paymentMetadata: PaymentMethodDisclaimerSignedMetadata) => {
    setPaymentMetadata(paymentMetadata);
    onOpen();
  };

  return { onOpenDisclaimerDialog, onClose, isOpen, disclaimerFormik, agreementAccepted, setAgreementAccepted, paymentMetadata };
}
